import { AKComponent } from '@theme/component';
import { morph } from '@theme/morph';

/**
 * Product Card Web Component
 * 
 * Gère uniquement les variantes de couleur des cartes produit
 */
export class ProductCard extends AKComponent {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {(() => void) | undefined} */
  #mobileSliderCleanup;

  constructor() {
    super();
    this.handle = this.getAttribute('handle');
    this.index = parseInt(this.getAttribute('data-index') || '0');
  }

  connectedCallback() {
    super.connectedCallback();
    this.setupVariantInputs();
    this.#setupMobileSlider();
  }

  disconnectedCallback() {
    this.#mobileSliderCleanup?.();
    super.disconnectedCallback();
  }

  /**
   * Ecoute les inputs de variantes (swatches) et déclenche une requête côté serveur
   */
  setupVariantInputs() {
    /** @type {HTMLInputElement[]} */
    const inputs = Array.isArray(this.refs.swatchInputs) ? /** @type {HTMLInputElement[]} */ (this.refs.swatchInputs) : [];

    inputs.forEach((input) => {
      input.addEventListener('change', (event) => {
        if (!(event.target instanceof HTMLInputElement)) return;
        const optionValueId = event.target.dataset.optionValueId || this.#extractOptionValueId(event.target);
        if (!optionValueId) return;
        // Mettre à jour l'état actif immédiatement pour réactivité
        const label = this.#findLabelForInput(event.target);
        if (label) this.updateActiveVariant(label);
        // Construire l'URL de requête et mettre à jour directement le lien
        const requestUrl = this.#buildRequestUrl(optionValueId);
        if (requestUrl) {
          this.#updateProductLink(requestUrl);
          this.#fetchAndUpdateSpecificElements(requestUrl);
        }
      });

      // Support clavier (Enter/Space) sur le label
      const label = this.#findLabelForInput(input);
      if (label) {
        label.addEventListener('keydown', (ev) => {
          const e = /** @type {KeyboardEvent} */ (ev);
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            input.click();
          }
        });
      }
    });
  }

  /**
   * Met à jour la variante active
   */
  /**
   * @param {HTMLElement} activeVariant
   */
  updateActiveVariant(activeVariant) {
    /** @type {HTMLElement[]} */
    const labels = Array.isArray(this.refs.swatchLabels) ? /** @type {HTMLElement[]} */ (this.refs.swatchLabels) : [];
    
    labels.forEach((label) => {
      label.classList.remove('active', 'c-swatch--selected');
      label.setAttribute('aria-selected', 'false');
    });

    activeVariant.classList.add('active', 'c-swatch--selected');
    activeVariant.setAttribute('aria-selected', 'true');
  }

  /**
   * Extrait l'id d'option depuis l'input id="swatch-{optionValueId}-{productId}"
   * @param {HTMLInputElement} input
   * @returns {string | null}
   */
  #extractOptionValueId(input) {
    const id = input.id || '';
    // format attendu: swatch-<optionValueId>-<productId>
    const parts = id.split('-');
    if (parts.length >= 3 && parts[0] === 'swatch') {
      return parts[1] || null;
    }
    return null;
  }

  /**
   * Trouve le label ref correspondant à l'input donné
   * @param {HTMLInputElement} input
   * @returns {HTMLElement | null}
   */
  #findLabelForInput(input) {
    /** @type {HTMLElement[]} */
    const labels = Array.isArray(this.refs.swatchLabels) ? /** @type {HTMLElement[]} */ (this.refs.swatchLabels) : [];
    const match = labels.find((l) => l.getAttribute('for') === input.id);
    return match || null;
  }

  /**
   * Lien principal vers la fiche produit (titre / lien étiré).
   * @returns {HTMLAnchorElement | null}
   */
  #getProductLink() {
    const refLink = this.refs.productLink;
    if (refLink instanceof HTMLAnchorElement) {
      return refLink;
    }
    const first = this.querySelector('a');
    return first instanceof HTMLAnchorElement ? first : null;
  }

  /**
   * Met à jour directement l'URL du lien produit
   * @param {string} requestUrl
   */
  #updateProductLink(requestUrl) {
    const currentLink = this.#getProductLink();
    if (currentLink && requestUrl) {
      currentLink.setAttribute('href', requestUrl);
    }
  }

  /**
   * Pastilles + scroll snap sur le carrousel mobile des cartes produit
   */
  #setupMobileSlider() {
    this.#mobileSliderCleanup?.();

    const root = this.querySelector('[data-mobile-product-slider]');
    if (!root) return;

    const viewport = root.querySelector('.c-pdt-mini__slider-viewport');
    const slides =
      viewport instanceof HTMLElement
        ? [...viewport.children].filter(
            (el) => el instanceof HTMLElement && el.classList.contains('c-pdt-mini__slider-slide')
          )
        : [];
    const dots = [...root.querySelectorAll('.c-pdt-mini__slider-dot')];
    if (!(viewport instanceof HTMLElement) || slides.length < 2 || dots.length < 2) return;

    const setActiveByIndex = (activeIndex) => {
      const clamped = Math.max(0, Math.min(slides.length - 1, Math.min(dots.length - 1, activeIndex)));
      for (let i = 0; i < slides.length; i++) {
        slides[i].classList.toggle('is-active', i === clamped);
      }
      for (let i = 0; i < dots.length; i++) {
        dots[i].classList.toggle('is-active', i === clamped);
        dots[i].setAttribute('aria-current', i === clamped ? 'true' : 'false');
      }
    };

    // IntersectionObserver: active le dot dès qu'un slide est visible à ≥50% dans le viewport scroll
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            const index = slides.indexOf(/** @type {HTMLElement} */ (entry.target));
            if (index !== -1) setActiveByIndex(index);
          }
        }
      },
      { root: viewport, threshold: 0.5 }
    );
    slides.forEach((slide) => io.observe(slide));

    // Clics sur les dots : scroll direct vers la position exacte du slide
    const abort = new AbortController();
    const { signal } = abort;
    for (let i = 0; i < dots.length; i++) {
      const index = i;
      dots[i].addEventListener(
        'click',
        () => {
          viewport.scrollTo({ left: index * viewport.clientWidth, behavior: 'smooth' });
        },
        { signal }
      );
    }

    /** @param {MouseEvent | KeyboardEvent} e */
    const goToProductFromSlider = (e) => {
      if (e.target instanceof Element && e.target.closest('.c-pdt-mini__slider-dot')) {
        return;
      }
      const link = this.#getProductLink();
      if (!link?.href) {
        return;
      }
      if (e instanceof MouseEvent && e.button !== 0) {
        return;
      }
      if (e instanceof MouseEvent && (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)) {
        window.open(link.href, '_blank', 'noopener,noreferrer');
        e.preventDefault();
        return;
      }
      e.preventDefault();
      link.click();
    };

    root.addEventListener('click', goToProductFromSlider, { signal });
    root.addEventListener(
      'keydown',
      (e) => {
        if (!(e instanceof KeyboardEvent) || e.key !== 'Enter') {
          return;
        }
        goToProductFromSlider(e);
      },
      { signal }
    );

    this.#mobileSliderCleanup = () => {
      io.disconnect();
      abort.abort();
    };
  }

  /**
   * Fetch les données et met à jour seulement les refs spécifiques
   * @param {string} requestUrl
   */
  #fetchAndUpdateSpecificElements(requestUrl) {
    fetch(requestUrl)
      .then((r) => r.text())
      .then((htmlText) => {
        const html = new DOMParser().parseFromString(htmlText, 'text/html');
        
        // Find the matching product-card in the response
        const selector = `product-card[handle="${this.handle}"]`;
        const newCard = html.querySelector(selector) || html.querySelector('product-card');
        
        if (!(newCard instanceof HTMLElement)) {
          return;
        }
        
        this.#updateSpecificRefs(newCard);
      })
      .catch((err) => {
        console.error('Failed to fetch and update:', err);
      });
  }

  /**
   * Met à jour seulement les refs spécifiques (image, prix) sans toucher au lien
   * @param {HTMLElement} newCard
   */
  #updateSpecificRefs(newCard) {
    // Update product image(s) — supports dual refs (mobile slider + desktop) via productImage[]
    const imageSelector = '[ref="productImage"], [ref="productImage[]"]';
    const currentWrappers = [...this.querySelectorAll(imageSelector)];
    const newWrappers = [...newCard.querySelectorAll(imageSelector)];
    const pairCount = Math.min(currentWrappers.length, newWrappers.length);
    for (let i = 0; i < pairCount; i++) {
      const currentImage = /** @type {HTMLElement} */ (currentWrappers[i]);
      const newImage = /** @type {HTMLElement} */ (newWrappers[i]);
      if (currentImage.tagName === 'IMG' && newImage.tagName === 'IMG') {
        /** @type {HTMLImageElement} */ (currentImage).src = /** @type {HTMLImageElement} */ (newImage).src;
        /** @type {HTMLImageElement} */ (currentImage).srcset = /** @type {HTMLImageElement} */ (newImage).srcset;
        /** @type {HTMLImageElement} */ (currentImage).alt = /** @type {HTMLImageElement} */ (newImage).alt;
      } else {
        const currentImg = currentImage.querySelector('img');
        const newImg = newImage.querySelector('img');
        if (currentImg && newImg) {
          currentImg.src = newImg.src;
          currentImg.srcset = newImg.srcset || '';
          currentImg.alt = newImg.alt || '';
        } else {
          currentImage.innerHTML = newImage.innerHTML;
        }
      }
    }

    // Update product price
    const currentPrice = /** @type {HTMLElement} */ (this.refs.productPrice);
    const newPrice = newCard.querySelector('[ref="productPrice"]');
    if (currentPrice && newPrice) {
      currentPrice.innerHTML = newPrice.innerHTML;
    }

    // Log the specific refs that were updated
  }

  /**
   * Construit l'URL de requête serveur pour re-render la card produit uniquement
   * @param {string} optionValueId
   * @returns {string | null}
   */
  #buildRequestUrl(optionValueId) {
    const datasetUrl = (this.dataset.productUrl || '').split('?')[0];
    // Support combined listing: prefer connected product url if present from the currently checked input
    /** @type {HTMLInputElement[]} */
    const allInputs = Array.isArray(this.refs.swatchInputs) ? /** @type {HTMLInputElement[]} */ (this.refs.swatchInputs) : [];
    const currentChecked = allInputs.find((i) => i.checked && !!i.dataset.connectedProductUrl);
    const connectedUrl = currentChecked && currentChecked.dataset.connectedProductUrl ? currentChecked.dataset.connectedProductUrl : null;
    const pendingUrl = this.#pendingRequestUrl ?? null;
    const finalUrl = connectedUrl || pendingUrl || datasetUrl || '';
    if (!finalUrl) return null;
    let productUrl = finalUrl.indexOf('?') >= 0 ? finalUrl.split('?')[0] : finalUrl;

    this.#pendingRequestUrl = productUrl;

    const params = new URLSearchParams();
    // Do not include section_id; fetch full page and select the matching product-card in the response
    // Construire la liste complète des option_values (toutes options sélectionnées)
    /** @type {string[]} */
    const selectedIds = allInputs
      .filter((i) => i.checked && !!i.dataset.optionValueId)
      .map((i) => i.dataset.optionValueId || '')
      .filter((v) => v !== '');
    if (!selectedIds.includes(optionValueId)) selectedIds.push(optionValueId);
    params.set('option_values', selectedIds.join(','));
    return `${productUrl}?${params.toString()}`;
  }

}

// Enregistrement du web component
customElements.define('product-card', ProductCard);